import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Receipt, Plus, Trash2, Download, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['travel', 'marketing', 'meals', 'office', 'technology', 'other'];
const CAT_COLORS = { travel: 'bg-blue-50 text-blue-700', marketing: 'bg-violet-50 text-violet-700', meals: 'bg-amber-50 text-amber-700', office: 'bg-emerald-50 text-emerald-700', technology: 'bg-cyan-50 text-cyan-700', other: 'bg-slate-50 text-slate-600' };
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);

export default function ExpensesTab() {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ description: '', amount: '', category: 'other', date: new Date().toISOString().slice(0, 10), notes: '' });
  const year = new Date().getFullYear();

  const fetch = useCallback(async () => {
    try {
      const [e, s] = await Promise.all([
        axios.get(`${API}/expenses`, { params: { year }, withCredentials: true }),
        axios.get(`${API}/expenses/summary`, { params: { year }, withCredentials: true }),
      ]);
      setExpenses(e.data); setSummary(s.data);
    } catch {}
  }, [year]);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    if (!form.description || !form.amount) { toast.error('Description and amount required'); return; }
    try {
      await axios.post(`${API}/expenses`, { ...form, amount: parseFloat(form.amount) }, { withCredentials: true });
      toast.success('Expense added'); setShowForm(false); setForm({ description: '', amount: '', category: 'other', date: new Date().toISOString().slice(0, 10), notes: '' }); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const remove = async (id) => { try { await axios.delete(`${API}/expenses/${id}`, { withCredentials: true }); toast.success('Deleted'); fetch(); } catch { toast.error('Failed'); } };

  return (
    <div data-testid="expenses-tab">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900">Expense Tracker</h1><p className="text-sm text-slate-400">Log business expenses for tax purposes. Export to CSV anytime.</p></div>
        <div className="flex gap-2">
          <a href={`${API}/expenses/export?year=${year}`} className="inline-flex items-center gap-1 text-xs border border-slate-200 px-3 py-1.5 rounded-lg text-slate-600 hover:bg-slate-50" data-testid="export-expenses-btn"><Download size={12} /> Export CSV</a>
          {!showForm && <Button onClick={() => setShowForm(true)} className="bg-[#002FA7] text-white text-xs gap-1.5 rounded-lg" data-testid="add-expense-btn"><Plus size={14} /> Add</Button>}
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-slate-200 p-4 rounded-xl"><div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Total ({year})</div><div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900">{fmt(summary.total)}</div><div className="text-xs text-slate-400">{summary.count} expenses</div></div>
          {Object.entries(summary.by_category || {}).slice(0, 3).map(([cat, amt]) => (
            <div key={cat} className="bg-white border border-slate-200 p-4 rounded-xl"><div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold capitalize">{cat}</div><div className="text-xl font-['Cabinet_Grotesk'] font-bold text-slate-700">{fmt(amt)}</div></div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="expense-form">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
            <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Description *</Label><Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Uber to showing" data-testid="expense-desc" /></div>
            <div><Label className="text-xs text-slate-500">Amount *</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} placeholder="25.00" data-testid="expense-amount" /></div>
            <div><Label className="text-xs text-slate-500">Category</Label><Select value={form.category} onValueChange={v => setForm(f => ({...f, category: v}))}><SelectTrigger className="text-xs capitalize"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-slate-500">Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} /></div>
            <div className="sm:col-span-3"><Label className="text-xs text-slate-500">Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} /></div>
          </div>
          <div className="flex gap-2"><Button onClick={save} className="bg-[#002FA7] text-white text-xs rounded-lg" data-testid="save-expense-btn">Add Expense</Button><Button variant="outline" onClick={() => setShowForm(false)} className="text-xs rounded-lg">Cancel</Button></div>
        </div>
      )}

      {expenses.length === 0 && !showForm ? (
        <div className="bg-white border border-slate-200 p-12 rounded-xl text-center"><Receipt size={40} className="text-slate-200 mx-auto mb-4" /><p className="text-slate-400">No expenses logged yet. Start tracking for tax season.</p></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {expenses.map(e => (
            <div key={e.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50/50" data-testid={`expense-${e.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="text-sm font-medium text-slate-800">{e.description}</span><Badge className={`text-[9px] capitalize ${CAT_COLORS[e.category] || ''}`}>{e.category}</Badge></div>
                {e.notes && <div className="text-xs text-slate-400">{e.notes}</div>}
              </div>
              <div className="text-right shrink-0"><div className="text-sm font-bold text-slate-800">{fmt(e.amount)}</div><div className="text-[10px] text-slate-400">{e.date}</div></div>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 shrink-0" onClick={() => remove(e.id)}><Trash2 size={12} /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
